import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

interface AcceptButtonProps {
    className?: string;
    isAccepted?: boolean;
    onClick?: any;
    [key: string]: any;
}

const AcceptIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_8177_17693)">
            <path
                d="M4.66634 6.66659V14.6666M9.99967 3.91992L9.33301 6.66659H13.2197C13.4267 6.66659 13.6308 6.71478 13.816 6.80735C14.0011 6.89992 14.1621 7.03432 14.2863 7.19992C14.4105 7.36551 14.4945 7.55775 14.5315 7.7614C14.5685 7.96506 14.5576 8.17454 14.4997 8.37325L12.9463 13.7066C12.8656 13.9835 12.6971 14.2268 12.4663 14.3999C12.2355 14.573 11.9548 14.6666 11.6663 14.6666H2.66634C2.31272 14.6666 1.97358 14.5261 1.72353 14.2761C1.47348 14.026 1.33301 13.6869 1.33301 13.3333V7.99992C1.33301 7.6463 1.47348 7.30716 1.72353 7.05711C1.97358 6.80706 2.31272 6.66659 2.66634 6.66659H4.50634C4.7544 6.66645 4.9975 6.59713 5.20831 6.4664C5.41912 6.33567 5.58929 6.14873 5.69967 5.92659L7.99967 1.33325C8.31406 1.33715 8.6235 1.41203 8.90488 1.55232C9.18625 1.6926 9.43229 1.89466 9.62461 2.14339C9.81693 2.39212 9.95055 2.68109 10.0155 2.98872C10.0804 3.29635 10.075 3.61468 9.99967 3.91992Z"
                stroke="#F8FAFC"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
        <defs>
            <clipPath id="clip0_8177_17693">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const AcceptedCheckmarkIcon = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
    >
        <path
            d="M9 3L4.5 7.5L3 6"
            stroke="#F8FAFC"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const AcceptedThumbsUpIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
    >
        <g clipPath="url(#clip0_8177_17693_accepted)">
            <path
                d="M4.66634 6.66659V14.6666M9.99967 3.91992L9.33301 6.66659H13.2197C13.4267 6.66659 13.6308 6.71478 13.816 6.80735C14.0011 6.89992 14.1621 7.03432 14.2863 7.19992C14.4105 7.36551 14.4945 7.55775 14.5315 7.7614C14.5685 7.96506 14.5576 8.17454 14.4997 8.37325L12.9463 13.7066C12.8656 13.9835 12.6971 14.2268 12.4663 14.3999C12.2355 14.573 11.9548 14.6666 11.6663 14.6666H2.66634C2.31272 14.6666 1.97358 14.5261 1.72353 14.2761C1.47348 14.026 1.33301 13.6869 1.33301 13.3333V7.99992C1.33301 7.6463 1.47348 7.30716 1.72353 7.05711C1.97358 6.80706 2.31272 6.66659 2.66634 6.66659H4.50634C4.7544 6.66645 4.9975 6.59713 5.20831 6.4664C5.41912 6.33567 5.58929 6.14873 5.69967 5.92659L7.99967 1.33325C8.31406 1.33715 8.6235 1.41203 8.90488 1.55232C9.18625 1.6926 9.43229 1.89466 9.62461 2.14339C9.81693 2.39212 9.95055 2.68109 10.0155 2.98872C10.0804 3.29635 10.075 3.61468 9.99967 3.91992Z"
                stroke="#F8FAFC"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
        <defs>
            <clipPath id="clip0_8177_17693_accepted">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

export default function AcceptButton({
    className,
    isAccepted = false,
    onClick,
    ...props
}: AcceptButtonProps) {
    const baseClasses = clsx(
        "inline-flex items-center justify-center",
        "transition-colors duration-200",
        "w-10 h-10 rounded-md text-sm font-medium shadow-sm",
        {
            "gap-0.5 bg-teal-600 text-white": isAccepted,
            "gap-2 px-2 py-2 bg-black text-white hover:bg-gray-900": !isAccepted,
        },
        className
    );

    return (
        <button
            className={baseClasses}
            onClick={onClick}
            disabled={isAccepted}
            {...props}
        >
            {isAccepted ? (
                <>
                    <AcceptedCheckmarkIcon />
                    <AcceptedThumbsUpIcon />
                </>
            ) : (
                <AcceptIcon />
            )}
        </button>
    );
}

AcceptButton.propTypes = {
    className: PropTypes.string,
    isAccepted: PropTypes.bool,
    onClick: PropTypes.func,
};
